/* eslint-disable max-len */
import { toastr } from 'react-redux-toastr';
import {
    pendingTask, // The action key for modifying loading state
    begin, // The action value if a "long" running task begun
    end, // The action value if a "long" running task ended
} from 'react-redux-spinner';
// eslint-disable-next-line import/no-unresolved
import { correctBrowserTimezone, getDateTimeFormats, formatUTCDateAsIs } from '@commonClient2/services/ukgDateTime';
import { UPDATE_DRAFT_EMPLOYEES_SELECTION } from '../bidProfileDraft';
import { UPDATE_EMPLOYEES_SELECTION } from '../bidProfile';
import { UPDATE_MANAGER_APP_DATA } from './appData.actions';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import i18n from '../../../services/i18n';
import {
    getAllDrafts,
    fetchAllBidSetups,
    getAllMyPublicHyperfinds,
    getAllMySymbolicPeriods,
    executeDataview,
    executeDataviewDraft,
    getAllBidProfiles,
    getAllBidProfilesArchived,
    getWorkplans,
    getAllAuditBids,
} from '../../services/api-endpoints';
import BriefBidProfileModelBuilder from '../../services/BriefBidProfileModelBuilder';
import { WorkplanStatus } from '../../../Enums';

export const fetchManagerAppData = () =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const [
            managerBidSetupsResponse,
            symbolicPeriodsResponse,
            hyperfindsResponse,
            bidProfileDraftsResponse,
            bidProfilesResponse,
            archivedBidProfilesResponse,                
        ] = await Promise.all([
            fetchAllBidSetups(),
            getAllMySymbolicPeriods(),
            getAllMyPublicHyperfinds(),
            getAllDrafts(),
            getAllBidProfiles(),
            getAllBidProfilesArchived()
        ]);

        /** @type {ManagerAppData.ISymbolicPeriod[]} */
        const periods = symbolicPeriodsResponse.data.map((x) => ({
            label: x.name,
            startDate: correctBrowserTimezone(new Date(x.startDate)) || (new Date()),
            endDate: correctBrowserTimezone(new Date(x.endDate)) || (new Date()),
        }));

        /** @type {BidProfile.IBriefBaseProfile[]} */
        const bidProfileDrafts = bidProfileDraftsResponse.data.map((x) => BriefBidProfileModelBuilder.fromBriefBidProfileDraftPresentationModel(
            x,
            {
                hyperfinds: hyperfindsResponse.data,
            },
        ));

        /** @type {BidProfile.IBriefBaseProfile[]} */
        const bidProfiles = bidProfilesResponse.data.map((x) => BriefBidProfileModelBuilder.fromBriefBidProfilePresentationModel(
            x,
            {
                hyperfinds: hyperfindsResponse.data,
            },
        ));

        /** @type {BidProfile.IBriefBaseProfile[]} */
        const archivedBidProfiles = archivedBidProfilesResponse.data.map((x) => BriefBidProfileModelBuilder.fromBriefBidProfilePresentationModel(
            x,
            {
                hyperfinds: hyperfindsResponse.data,
            },
        ));

        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: {
                managerBidSetups: managerBidSetupsResponse.data,
                periods,
                hyperfinds: hyperfindsResponse.data,
                bidProfileDrafts,
                bidProfiles,
                archivedBidProfiles,
                isAppDataLoaded: true,
            },
        });
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingAppData'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {ManagerAppData.IAppData} currentAppData
 */
export const fetchPartialManagerAppData = (currentAppData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    if (currentAppData.isAppDataLoaded) {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        try {
            const [
                managerBidSetupsResponse,
                bidProfileDraftsResponse,
                bidProfilesResponse,
            ] = await Promise.all([
                fetchAllBidSetups(),
                getAllDrafts(),
                getAllBidProfiles(),
            ]);

            /** @type {BidProfile.IBriefBaseProfile[]} */
            const bidProfileDrafts = bidProfileDraftsResponse.data.map((x) => BriefBidProfileModelBuilder.fromBriefBidProfileDraftPresentationModel(
                x,
                {
                    hyperfinds: currentAppData.hyperfinds,
                },
            ));
            /** @type {BidProfile.IBriefBaseProfile[]} */
            const bidProfiles = bidProfilesResponse.data.map((x) => BriefBidProfileModelBuilder.fromBriefBidProfilePresentationModel(
                x,
                {
                    hyperfinds: currentAppData.hyperfinds,
                },
            ));
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    managerBidSetups: managerBidSetupsResponse.data, 
                    bidProfileDrafts,
                    bidProfiles,
                },
            });
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.reloadingAppData'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    }
};

/**
 * @param {boolean} isDraft
 * @param {string} profileId
 * @param {number|undefined} hyperfindId
 * @param {ManagerAppData.ISelectedPeriod} selectedPeriod
 * @param {number|undefined} bidSetupId
 * @param {{
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const selectPeriod = (isDraft, profileId, hyperfindId, selectedPeriod, bidSetupId, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    if (selectedPeriod && hyperfindId) {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const employeeTableInfo = isDraft
                ? await executeDataviewDraft(profileId, hyperfindId, selectedPeriod.startDate, selectedPeriod.endDate)
                : await executeDataview(profileId, hyperfindId, selectedPeriod.startDate, selectedPeriod.endDate);

            const setup = storeData.managerBidSetups.find((x) => x.bidSetupId === bidSetupId);
            const dataviewFieldsSelected = new Set(setup?.dataviewDisplayedCols);
            const isSelectedInBidSetup = (key) => {
                if (!setup) {
                    return true; // all dataview selected by default
                }
                return dataviewFieldsSelected?.has(key);
            };
            const employees = employeeTableInfo.data.map((x) => ({ employeeId: x.employeeId, employeeName: x.employeeName, dataviewAttributes: x.dataview.ANY.attributes }));
            const filteredEmployees = employeeTableInfo.data.map((x) => ({
                employeeId: x.employeeId,
                employeeName: x.employeeName,
                dataviewAttributes: x.dataview.ANY.attributes.filter((attribute) => isSelectedInBidSetup(attribute.key)),
            }));
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    dataviewEmployeeData: employees,
                    filteredDataviewEmployeeData: filteredEmployees,
                    dataviewFields: employeeTableInfo.data[0]?.dataview.ANY.attributes.map((x) => ({
                        label: x.label,
                        key: x.key,
                        value: x.key,
                        checked: isSelectedInBidSetup(x.key),
                    })) || [],
                },
            });
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.retrievingEmployeesDataviews'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    }
    if (isDraft) {
        dispatch({
            type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
            payload: {
                periodSelected: selectedPeriod,
            },
        });
    } else {
        dispatch({
            type: UPDATE_EMPLOYEES_SELECTION,
            payload: {
                periodSelected: selectedPeriod,
            },
        });
    }
};

/**
 *
 * @param {string} id
 * @param {boolean} isDraft
 * @param {number|undefined} hyperfindId
 * @param {string|undefined} hyperfindLabel
 * @param {ManagerAppData.ISelectedPeriod} selectedPeriod
 * @param {number|undefined} bidSetupId
 * @param {{
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const selectHyperfind = (isDraft, id, hyperfindId, hyperfindLabel, selectedPeriod, bidSetupId, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    /** @type number[] */
    let employeeIds = [];
    if (selectedPeriod && hyperfindId) {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        try {
            const employeeTableInfo = isDraft
                ? await executeDataviewDraft(id, hyperfindId, selectedPeriod.startDate, selectedPeriod.endDate)
                : await executeDataview(id, hyperfindId, selectedPeriod.startDate, selectedPeriod.endDate);

            const setup = storeData.managerBidSetups.find((x) => x.bidSetupId === bidSetupId);
            const dataviewFieldsSelected = new Set(setup?.dataviewDisplayedCols);
            const isSelectedInBidSetup = (/** @type {string} */ key) => {
                if (!setup) {
                    return true; // all dataview selected by default
                }
                return dataviewFieldsSelected?.has(key);
            };
            const getDataviewAttributes = (/** @type {ApiManager.IDataviewEmployeeExecution} */x) => x?.dataview.ANY?.attributes || [];
            const employees = employeeTableInfo.data.map((x) => ({
                employeeId: x.employeeId,
                employeeName: x.employeeName,
                dataviewAttributes: getDataviewAttributes(x),
            }));
            const filteredEmployees = employeeTableInfo.data.map((x) => ({
                employeeId: x.employeeId,
                employeeName: x.employeeName,
                dataviewAttributes: getDataviewAttributes(x).filter((attribute) => isSelectedInBidSetup(attribute.key)),
            }));
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    dataviewEmployeeData: employees,
                    filteredDataviewEmployeeData: filteredEmployees,
                    dataviewFields: getDataviewAttributes(employeeTableInfo.data[0]).map((x) => ({
                        label: x.label,
                        key: x.key,
                        value: x.key,
                        checked: isSelectedInBidSetup(x.key),
                    })),
                },
            });
            employeeIds = employees.map((x) => x.employeeId);
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.retrievingEmployeesDataviews'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    }
    if (isDraft) {
        dispatch({
            type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
            payload: {
                hyperfindId,
                hyperfindLabel,
                employeesIds: employeeIds,
            },
        });
    } else {
        dispatch({
            type: UPDATE_EMPLOYEES_SELECTION,
            payload: {
                hyperfindId,
                hyperfindLabel,
                employeesIds: employeeIds,
            },
        });
    }
};

/**
 * @param {boolean} isDraft
 * @param {{
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @param {BidProfile.IBaseProfile} profile
 */
export const loadEmployees = (isDraft, profile, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    if (!profile.originalEmployees.hyperfindId || !profile.originalEmployees.periodSelected.startDate || !profile.originalEmployees.periodSelected.endDate) {
        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: {
                dataviewEmployeeData: [],
                filteredDataviewEmployeeData: [],
                dataviewFields: [],
            },
        });
        return;
    }
    const { hyperfindId, periodSelected } = profile.originalEmployees;
    const { managerBidSetups } = storeData;

    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const employeeTableInfo = isDraft
            ? await executeDataviewDraft(profile.id, hyperfindId, periodSelected.startDate, periodSelected.endDate)
            : await executeDataview(profile.id, hyperfindId, periodSelected.startDate, periodSelected.endDate);
        const setup = managerBidSetups.find((x) => x.bidSetupId === profile.originalBidProfileInformation.bidSetupId);
        const dataviewFieldsSelected = new Set(setup?.dataviewDisplayedCols);

        const employees = employeeTableInfo.data.map((x) => ({
            employeeId: x.employeeId,
            employeeName: x.employeeName,
            dataviewAttributes: x.dataview.ANY.attributes,
        }));

        const filteredEmployees = employeeTableInfo.data.map((x) => ({
            employeeId: x.employeeId,
            employeeName: x.employeeName,
            dataviewAttributes: x.dataview.ANY.attributes.filter((attribute) => dataviewFieldsSelected?.has(attribute.key)),
        }));

        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: {
                dataviewEmployeeData: employees,
                filteredDataviewEmployeeData: filteredEmployees,
                dataviewFields: employeeTableInfo.data[0]?.dataview.ANY.attributes.map((x) => ({
                    label: x.label, key: x.key, value: x.key, checked: dataviewFieldsSelected?.has(x.key),
                })) || [],
            },
        });
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingEmployees'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {number | null} d
 * @returns {boolean}
*/
const isForever = (d) => d === 32503680000;

/**
 * @param {ApiManager.IWorkplanBriefPresentationModel} workplan
 * @returns {string | undefined}
 */
const generateWorkplanSecondaryLabel = (workplan) => {
    if (workplan.workplanStatus === 0) {
        return undefined;
    }

    const userDateTimeFormats = getDateTimeFormats();

    const startDate = formatUTCDateAsIs(
        // @ts-ignore
        new Date(workplan.startDate * 1000),
        userDateTimeFormats.LongDateFormat,
    );
    const endDate = isForever(workplan.endDate)
        ? i18n.t('shiftBidding:form.activeWorkplanDates.forever')
        : formatUTCDateAsIs(
            // @ts-ignore
            new Date(workplan.endDate * 1000),
            userDateTimeFormats.LongDateFormat,
        );

    return `${startDate} - ${endDate}`;
};

/**
 * @param {ApiManager.IWorkplanBriefPresentationModel} workplan
 * @returns {ManagerAppData.IWorkplanBrief}
 */
export const formatWorkplan = (workplan) => ({
    id: workplan.workplanId,
    label: workplan.name,
    secondaryLabel: generateWorkplanSecondaryLabel(workplan),
    selected: false,
    type: workplan.periodType,
    length: workplan.periodLength,
    anchorDay: workplan.anchorDay,
    disabled: false,
    checked: false,
    startDate: workplan.startDate,
    endDate: workplan.endDate,
    workplanStatus: workplan.workplanStatus,
});

/**
 * @param {string} bidProfileId
 * @param {boolean?} isShowActiveWorkplanOnly
 */
export const loadWorkplans = (bidProfileId, isShowActiveWorkplanOnly = false) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const workplanStatus = isShowActiveWorkplanOnly ? WorkplanStatus.Played : WorkplanStatus.NotStarted;
        const workplansResponse = await getWorkplans(bidProfileId, workplanStatus);
        if (workplansResponse.status === 200) {
            /** @type {ManagerAppData.IWorkplanBrief[]} */
            const workplans = workplansResponse.data.map((x) => formatWorkplan(x));
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    workplans,
                },
            });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.loadingWorkplans'), i18n.t('shiftBidding:toastr.failedToLoad'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingWorkplans'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

export const loadAuditReportBids = () =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const response = await getAllAuditBids();

        const auditBids = response.data;
        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: {
                auditBids,
            },
        });
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingAuditReportBids'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
